'use client';

/**
 * ConditionNode — a React Flow node for a `branch` step (spec §5/§6).
 *
 * Shows the condition as a plain-English "If the candidate …" + its timing (Wait-Until or
 * Within Nd) and the two labelled forks. The Yes/No edges are drawn + labelled by
 * the editor; here each fork carries a "+" to add a step at the head of that
 * branch. Coral accents the selected node only. Engine reads `node.branches`;
 * the recruiter never wires the fork by hand.
 */

import { memo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { GitBranch, Clock, Plus, Trash2 } from 'lucide-react';
import type { StepNode as StepNodeModel } from '../types';
import { CONDITION_LABELS, MIN_WITHIN_DAYS } from '../sequence-tree';

export type SequenceConditionNodeData = {
    node: StepNodeModel;
    selected: boolean;
    onSelect: (id: string) => void;
    /** Opens the step-type picker anchored at the click; inserts at the head of the fork. */
    onAddToBranch: (e: React.MouseEvent, id: string, side: 'yes' | 'no') => void;
    onRemove: (id: string) => void;
};

const WARM_GRAY = '#C8C3BA';

function timingText(node: StepNodeModel): string {
    const t = node.condition?.timing;
    if (!t) return '';
    if (t.mode === 'wait_until') return 'waits until it happens';
    const days = Math.max(Number(t.windowDays) || MIN_WITHIN_DAYS, MIN_WITHIN_DAYS);
    return `within ${days} day${days === 1 ? '' : 's'}`;
}

function ConditionNodeInner({ data }: NodeProps) {
    const d = data as unknown as SequenceConditionNodeData;
    const { node, selected } = d;
    const condText = node.condition ? CONDITION_LABELS[node.condition.type] : 'a condition is met';
    const stop = (e: React.MouseEvent) => e.stopPropagation();

    return (
        <div
            onClick={() => d.onSelect(node.id)}
            className="group relative flex cursor-pointer flex-col gap-2 px-3.5 py-3 transition-all"
            style={{
                width: 232,
                minHeight: 96,
                borderRadius: 14,
                background: 'var(--surface-secondary)',
                border: `${selected ? 2 : 1.5}px ${selected ? 'solid' : 'dashed'} ${selected ? 'var(--accent)' : WARM_GRAY}`,
                boxShadow: selected
                    ? '0 0 0 3px rgba(253,119,94,0.15), 0 2px 8px rgba(120,90,60,0.08)'
                    : '0 1px 3px rgba(120,90,60,0.06)',
            }}
        >
            <Handle
                type="target"
                position={Position.Top}
                style={{ width: 8, height: 8, background: WARM_GRAY, border: '2px solid white', top: -5 }}
            />

            <div className="flex items-center gap-2">
                <div
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md"
                    style={{
                        background: selected ? 'var(--accent-muted)' : 'rgba(200,195,186,0.3)',
                        color: selected ? 'var(--accent-emphasis)' : 'var(--text-secondary)',
                    }}
                >
                    <GitBranch size={13} strokeWidth={2} />
                </div>
                <span
                    className="text-[10px] font-semibold tracking-[0.06em] uppercase"
                    style={{ color: 'var(--text-tertiary)' }}
                >
                    Branch
                </span>

                <button
                    type="button"
                    aria-label="Remove branch"
                    title="Remove branch"
                    onClick={(e) => {
                        stop(e);
                        d.onRemove(node.id);
                    }}
                    className="nodrag nopan ml-auto flex h-6 w-6 items-center justify-center rounded-md opacity-0 transition-all group-hover:opacity-100"
                    style={{ color: 'var(--text-tertiary)' }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--bg-danger)';
                        e.currentTarget.style.color = 'var(--fg-danger)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = 'var(--text-tertiary)';
                    }}
                >
                    <Trash2 size={12} strokeWidth={2} />
                </button>
            </div>

            <div className="text-[12px] leading-snug" style={{ color: 'var(--text-primary)' }}>
                If the candidate <span className="font-semibold">{condText}</span>
                <span className="flex items-center gap-1 text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                    <Clock size={10} strokeWidth={1.75} /> {timingText(node)}
                </span>
            </div>

            <Handle
                type="source"
                position={Position.Bottom}
                style={{ width: 8, height: 8, background: WARM_GRAY, border: '2px solid white', bottom: -5 }}
            />

            {/* add-to-branch affordances — hover-revealed; left feeds the Yes
                edge, right feeds the No edge (both labelled on the canvas). */}
            <div
                className="absolute -bottom-3.5 right-0 left-0 flex items-center justify-between px-5 opacity-0 transition-opacity group-hover:opacity-100"
                style={{ pointerEvents: 'auto' }}
            >
                <BranchAddBtn
                    label="Add step to Yes branch"
                    onClick={(e) => {
                        stop(e);
                        d.onAddToBranch(e, node.id, 'yes');
                    }}
                />
                <BranchAddBtn
                    label="Add step to No branch"
                    onClick={(e) => {
                        stop(e);
                        d.onAddToBranch(e, node.id, 'no');
                    }}
                />
            </div>
        </div>
    );
}

function BranchAddBtn({ label, onClick }: { label: string; onClick: (e: React.MouseEvent) => void }) {
    return (
        <button
            type="button"
            aria-label={label}
            title={label}
            onClick={onClick}
            className="nodrag nopan flex h-6 w-6 items-center justify-center rounded-md transition-colors"
            style={{
                background: 'var(--surface-primary)',
                border: '1px solid var(--border-emphasis)',
                color: 'var(--text-secondary)',
                boxShadow: 'var(--shadow-sm)',
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--surface-hover)';
                e.currentTarget.style.borderColor = 'var(--accent)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.background = 'var(--surface-primary)';
                e.currentTarget.style.borderColor = 'var(--border-emphasis)';
            }}
        >
            <Plus size={12} strokeWidth={2.25} />
        </button>
    );
}

export const ConditionNode = memo(ConditionNodeInner);
